import { useEffect, useState } from 'react';
import axios from 'axios';

import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { useDialog } from '../components/Dialog';
import { Footer } from '../components/Footer';
import { ComparisonTable } from '../components/pricing/ComparisonTable';
import { FaqSection } from '../components/pricing/FaqSection';
import { CreditPackSection } from '../components/pricing/CreditPackSection';
import { PaidTierCard } from '../components/pricing/PaidTierCard';
import { StatusUnavailable } from '../components/StatusUnavailable';
import {
  TIER_ORDER,
  capitalize,
  getTierPrice,
  getAnnualSavings,
  getCreditsForTier,
} from '../config/pricingConfig';
import {
  captureFunnelEvent,
  trackPageView,
  trackButtonClick,
  reactPlugin,
} from '../services/telemetry';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { useIsAuthenticated, useMsal } from 'src/auth/firebaseMsalCompat';
import { useDebounceCallback } from 'usehooks-ts';
import { loginRequest } from '../config/authConfig';
import { getApiHost } from '../utils/getApiHost';

type SubscriptionTier = 'starter' | 'pro';
type BillingPeriod = 'monthly' | 'annual';

const Pricing = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginButtonLoading, setIsLoginButtonLoading] = useState(false);

  // Dialog hook for custom popups
  const { showAlert, showConfirm, DialogComponent } = useDialog();

  // Declared up here because `handleSubscribe` reads the entitlement before it
  // will act on a plan change.
  const { subscriptionStatus, entitlement, retryStatus, refreshStatus } =
    useSubscriptionStatus(isAuthenticated);

  // Billing period toggle (default to annual for better ARPU)
  const [isAnnual, setIsAnnual] = useState(true);

  const debouncedLogin = useDebounceCallback(
    async () => {
      setIsLoginButtonLoading(true);
      await trackButtonClick('Pricing', 'Log In');
      await instance.loginRedirect();
    },
    100,
    { trailing: true }
  );

  const handleContactUsClick = async () => {
    await trackButtonClick('Pricing', 'Contact Us - Scroll to Footer');
    // Scroll to the footer section
    const footer = document.querySelector('footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubscribe = async (tier: SubscriptionTier) => {
    if (isLoading) return;

    const billingPeriod: BillingPeriod = isAnnual ? 'annual' : 'monthly';
    const subscriptionPaymentProps = {
      tier,
      period: billingPeriod,
      product: 'subscription' as const,
    };

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      setIsLoginButtonLoading(true);
      await trackButtonClick('Pricing', `Subscribe ${tier} - Login Required`);
      captureFunnelEvent('payment_button_clicked', {
        ...subscriptionPaymentProps,
        login_required: true,
      });
      await instance.loginRedirect();
      return;
    }

    // Never act on an entitlement we don't know. Every decision below used to read
    // `subscriptionStatus?.…`, which silently reads a failed fetch as "no plan,
    // not from a store" — a second checkout for someone who already pays, or a
    // Stripe flow aimed at an Apple/Google subscription. Retry once, and bind the
    // answer to a non-nullable local so no later edit can reintroduce the guess.
    // Mirrors `handleGetPlan` in the app's `plan.tsx`.
    setIsLoading(true);
    const status =
      entitlement === 'known' ? subscriptionStatus : await refreshStatus();
    if (!status) {
      setIsLoading(false);
      await showAlert(
        'We couldn’t check your plan',
        "We couldn't confirm your current subscription, so we've stopped here rather than risk charging you twice. Please check your connection and try again in a moment."
      );
      return;
    }

    // Subscriptions bought in the mobile app are billed by Apple/Google, not
    // Stripe — the web change-plan/checkout flow can't act on them.
    if (status.source === 'apple' || status.source === 'google') {
      setIsLoading(false);
      await showAlert(
        'Manage in the mobile app',
        'You subscribed through the Pineapple Music Lab mobile app. Please manage or change your subscription in the mobile app.'
      );
      return;
    }

    try {
      await trackButtonClick('Pricing', `Subscribe ${tier} ${billingPeriod}`);
      captureFunnelEvent('payment_button_clicked', {
        ...subscriptionPaymentProps,
        login_required: false,
      });

      const authResult = await instance.acquireTokenSilent({ ...loginRequest });
      const apiUrl = getApiHost();

      // Check if user already has an active subscription
      const hasActiveSubscription =
        status.subscriptionTier === 'starter' ||
        status.subscriptionTier === 'pro';

      if (hasActiveSubscription) {
        // Determine if this is an upgrade
        const currentTierLevel = TIER_ORDER[status.subscriptionTier || 'free'];
        const newTierLevel = TIER_ORDER[tier];
        const isUpgrade = newTierLevel > currentTierLevel;
        const isFromAnnual = status.billingPeriod === 'annual';

        // Block Annual → Monthly changes
        if (isFromAnnual && billingPeriod === 'monthly') {
          await showAlert(
            'Cannot Change to Monthly',
            <>
              <p className="mb-3">
                Annual plans cannot be changed to monthly plans.
              </p>
              <p className="font-medium">You can:</p>
              <ul className="mt-2 list-inside list-disc">
                <li>Upgrade to a different annual plan</li>
                <li>Wait until your annual period ends to switch to monthly</li>
              </ul>
            </>
          );
          setIsLoading(false);
          return;
        }

        // Show confirmation dialog for upgrades
        if (isUpgrade) {
          const price = getTierPrice(tier, billingPeriod);
          const credits = getCreditsForTier(tier);
          const tierName = capitalize(tier);

          const confirmed = await showConfirm(
            `Upgrade to ${tierName}?`,
            <>
              <ul className="mb-4 list-inside list-disc space-y-1">
                <li>Your billing cycle will reset to today</li>
                <li>
                  Your monthly credits will be refilled to {credits}
                  {isFromAnnual ? ' now' : ''}
                </li>
              </ul>
              <p className="font-medium">Pricing</p>
              {isFromAnnual ? (
                <p className="mt-1 text-sm">
                  You will be charged{' '}
                  <span className="font-semibold">{price}</span> minus a
                  prorated cost for your unused annual time.
                </p>
              ) : (
                <p className="mt-1 text-sm">
                  You will be charged{' '}
                  <span className="font-semibold">{price}</span> now.
                </p>
              )}
            </>,
            { confirmText: 'Upgrade', cancelText: 'Cancel' }
          );

          if (!confirmed) {
            setIsLoading(false);
            return;
          }
        }

        // Show confirmation dialog for billing period change (Monthly → Annual, same tier)
        const isBillingPeriodChange =
          !isUpgrade &&
          status.subscriptionTier === tier &&
          status.billingPeriod === 'monthly' &&
          billingPeriod === 'annual';

        if (isBillingPeriodChange) {
          const annualPrice = getTierPrice(tier, 'annual');
          const monthlySavings = `$${getAnnualSavings(tier)}`;
          const tierName = capitalize(tier);

          const confirmed = await showConfirm(
            `Switch to Annual Billing?`,
            <>
              <p className="mb-3">
                Switch your {tierName} plan from monthly to annual billing.
              </p>
              <ul className="mb-4 list-inside list-disc space-y-1">
                <li>
                  Save{' '}
                  <span className="font-semibold">{monthlySavings}/year</span>{' '}
                  with annual billing
                </li>
                <li>Change takes effect at your next billing date</li>
                <li>Your current credits will remain unchanged</li>
              </ul>
              <p className="font-medium">Pricing</p>
              <p className="mt-1 text-sm">
                You will be charged{' '}
                <span className="font-semibold">{annualPrice}</span> at your
                next billing date.
              </p>
            </>,
            { confirmText: 'Switch to Annual', cancelText: 'Cancel' }
          );

          if (!confirmed) {
            setIsLoading(false);
            return;
          }
        }

        // Show confirmation dialog for downgrades (Pro → Starter)
        const isDowngrade = newTierLevel < currentTierLevel;

        if (isDowngrade) {
          const price = getTierPrice(tier, billingPeriod);
          const newTierName = capitalize(tier);

          const confirmed = await showConfirm(
            `Downgrade to ${newTierName}?`,
            <>
              <p className="mb-3">
                Your plan will change to {newTierName} at the end of your
                current billing period.
              </p>
              <ul className="mb-4 list-inside list-disc space-y-1">
                <li>You&apos;ll keep your current Pro benefits until then</li>
                <li>
                  Your credits will reset to {getCreditsForTier(tier)} at your
                  next billing date
                </li>
              </ul>
              <p className="font-medium">Pricing</p>
              <p className="mt-1 text-sm">
                You will be charged{' '}
                <span className="font-semibold">{price}</span> at your next
                billing date.
              </p>
            </>,
            { confirmText: 'Downgrade', cancelText: 'Cancel' }
          );

          if (!confirmed) {
            setIsLoading(false);
            return;
          }
        }

        // Use /change-plan endpoint for existing subscribers
        const response = await axios.post(
          `${apiUrl}/user/change-plan`,
          {
            new_tier: tier,
            billing_period: billingPeriod,
          },
          {
            headers: { Authorization: `Bearer ${authResult.idToken}` },
          }
        );

        // Handle response based on upgrade vs downgrade
        if (response.data.effective_immediately) {
          // Immediate upgrade - reload to show new status
          window.location.reload();
        } else {
          // Scheduled change - show message with effective date, then reload to show schedule
          await showAlert('Plan Change Scheduled', response.data.message);
          window.location.reload();
        }
      } else {
        // Use /subscribe endpoint for new subscribers
        // {CHECKOUT_SESSION_ID} is a Stripe template variable replaced with the real session ID on redirect
        const response = await axios.post(
          `${apiUrl}/user/subscribe`,
          {
            tier,
            billing_period: billingPeriod,
            success_url: `${window.location.origin}/pricing?session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: window.location.href,
          },
          {
            headers: { Authorization: `Bearer ${authResult.idToken}` },
          }
        );

        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_session_url;
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      await showAlert(
        'Subscription Error',
        'Something went wrong while processing your request. Please try again.'
      );
      setIsLoading(false);
    }
  };

  const handleBuyCredits = async () => {
    if (isLoading) return;

    const creditPackPaymentProps = {
      tier: 'credit_pack' as const,
      period: 'one_time' as const,
      product: 'credit_pack' as const,
    };

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      setIsLoginButtonLoading(true);
      await trackButtonClick('Pricing', 'Buy Credits - Login Required');
      captureFunnelEvent('payment_button_clicked', {
        ...creditPackPaymentProps,
        login_required: true,
      });
      await instance.loginRedirect();
      return;
    }

    try {
      setIsLoading(true);
      await trackButtonClick('Pricing', 'Buy Credit Pack');
      captureFunnelEvent('payment_button_clicked', {
        ...creditPackPaymentProps,
        login_required: false,
      });

      const authResult = await instance.acquireTokenSilent({ ...loginRequest });
      const apiUrl = getApiHost();

      // {CHECKOUT_SESSION_ID} is a Stripe template variable replaced with the real session ID on redirect
      const response = await axios.post(
        `${apiUrl}/user/buy-credits`,
        {
          success_url: `${window.location.origin}/pricing?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: window.location.href,
        },
        {
          headers: { Authorization: `Bearer ${authResult.idToken}` },
        }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_session_url;
    } catch (error) {
      console.error('Buy credits error:', error);
      setIsLoading(false);
    }
  };

  // Reset loading state when page is shown (e.g., after navigation)
  useEffect(() => {
    const handlePageShow = () => {
      setIsLoading(false);
      setIsLoginButtonLoading(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // SEO: per-page meta tags, canonical, OG & Twitter
  usePageMeta(getPublicPageMeta('/pricing'));

  useEffect(() => {
    trackPageView('Pricing', '/pricing');
  }, []);

  // True while authenticated but subscription data hasn't arrived yet. Only
  // `pending` counts: a failed read used to land here too, which pinned every CTA
  // in a loading state with no retry, so a paying user could neither manage their
  // plan nor buy anything until they thought to reload.
  const isStatusFetching = isAuthenticated && entitlement === 'pending';
  const isStatusFailed = isAuthenticated && entitlement === 'failed';

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col bg-[#fbfcfc]">
      <DialogComponent />
      <main className="flex-grow px-4 py-4 font-[Outfit] sm:px-6">
        <div className="flex flex-col items-center">
          <header className={`mb-6 w-full max-w-6xl text-left sm:mb-8`}>
            <h1
              className={`
                mt-4 mb-3 text-3xl font-light text-[#0d0d0d]
                sm:mt-6 sm:mb-4 sm:text-4xl
                lg:mt-8 lg:text-[40px]
              `}
            >
              Pricing
            </h1>
            <p className="text-base font-light text-[#0d0d0d] sm:text-lg">
              Generate piano fingerings with a plan that fits your needs.
            </p>
          </header>

          {/* Subscription status (plan, credits, billing) now lives on the
              Profile page (/profile). Pricing is plan selection only. */}

          {/* The pricing survey now runs in the generation-wait survey
              rotation (see WaitingSurveyCarousel), not on this page. */}

          {/* Billing Period Toggle */}
          <div className={`mt-6 mb-6 flex justify-center sm:mt-8 sm:mb-8`}>
            <div
              className={`
                inline-flex items-center gap-1 rounded-lg
                bg-[rgba(75,76,76,0.1)] p-1
              `}
              role="group"
              aria-label="Billing period"
            >
              <button
                onClick={() => setIsAnnual(false)}
                aria-pressed={!isAnnual}
                className={`
                  cursor-pointer rounded-[4px] px-6 py-2 text-[15px] text-[#333]
                  transition-colors duration-150
                  ${
                    !isAnnual
                      ? 'border border-[#333] bg-white'
                      : 'border border-transparent hover:bg-white/40'
                  }
                `}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                aria-pressed={isAnnual}
                className={`
                  flex cursor-pointer items-center gap-2 rounded-[4px] px-6 py-2
                  text-[15px] text-[#333] transition-colors duration-150
                  ${
                    isAnnual
                      ? 'border border-[#333] bg-white'
                      : 'border border-transparent hover:bg-white/40'
                  }
                `}
              >
                Annual
                <span
                  className={`
                    rounded-[4px] bg-[#333] px-2 py-0.5 text-xs text-[#ffec83]
                  `}
                >
                  Save 20%
                </span>
              </button>
            </div>
          </div>

          {/* A failed status read leaves the cards priced but unable to say which
              plan is the user's current one, and `handleSubscribe` will refuse
              rather than risk a duplicate checkout — so offer the retry here
              instead of letting them find out by tapping. */}
          {isStatusFailed && (
            <StatusUnavailable
              message="We couldn't check your current plan, so plan changes are paused for a moment."
              onRetry={retryStatus}
              className="mb-4 max-w-6xl"
            />
          )}

          {/* Pricing Cards - Always visible */}
          <div
            className={`
              grid w-full max-w-6xl grid-cols-1 gap-4
              md:grid-cols-2 md:gap-6
              lg:grid-cols-3
            `}
          >
            {/* Starter */}
            <PaidTierCard
              tier="starter"
              isAnnual={isAnnual}
              isLoading={isLoading || isStatusFetching}
              subscriptionStatus={subscriptionStatus}
              onSubscribe={handleSubscribe}
            />

            {/* Pro */}
            <PaidTierCard
              tier="pro"
              isAnnual={isAnnual}
              isLoading={isLoading || isStatusFetching}
              highlight
              subscriptionStatus={subscriptionStatus}
              onSubscribe={handleSubscribe}
            />

            {/* Business */}
            <div
              className={`
                flex flex-col rounded-[30px] bg-[rgba(210,131,255,0.2)] p-6
              `}
            >
              <h2 className={`my-3 pl-2 text-base font-light text-[#0d0d0d]`}>
                Business
              </h2>
              <p
                className={`
                  my-3 pl-2 text-[32px] leading-tight font-light text-[#0d0d0d]
                `}
              >
                Custom
              </p>
              <div className="flex-grow">
                <p className={`p-2 text-sm text-[#333]`}>
                  Contact us at{' '}
                  <a
                    href="mailto:hello@pineapplemusiclab.com"
                    className="text-[#333] underline hover:text-gray-900"
                    onClick={handleContactUsClick}
                  >
                    hello@pineapplemusiclab.com
                  </a>
                </p>
                <p className={`p-2 text-sm text-[#333]`}>- API access</p>
                <p className={`p-2 text-sm text-[#333]`}>- Batch processing</p>
                <p className={`p-2 text-sm text-[#333]`}>- Dedicated support</p>
              </div>
              <a
                href="mailto:hello@pineapplemusiclab.com"
                onClick={handleContactUsClick}
                className={`
                  mt-4 w-full cursor-pointer rounded-full border border-[#333]
                  bg-white px-4 py-2.5 text-center text-[15px] text-[#333]
                  transition-all duration-100
                  hover:bg-gray-100
                  active:scale-95
                `}
              >
                Contact us
              </a>
            </div>
          </div>

          {/* Credit Pack Section */}
          <CreditPackSection
            onBuyCredits={handleBuyCredits}
            isLoading={isLoading || isStatusFetching}
          />

          {/* Comparison Table */}
          <ComparisonTable />

          {/* FAQ Section */}
          <FaqSection />

          {/* Login Button - Only for non-authenticated users */}
          {!isAuthenticated && (
            <div className="px-4">
              <button
                onClick={debouncedLogin}
                className={`
                  mt-4 mb-8 cursor-pointer rounded-lg bg-yellow-400 px-6 py-2
                  text-lg font-semibold text-white shadow-md
                  hover:bg-yellow-500
                  focus:outline-hidden
                  active:scale-95
                  sm:mt-6 sm:mb-10 sm:px-8 sm:py-3 sm:text-xl
                  lg:text-2xl
                  ${isLoginButtonLoading ? 'cursor-not-allowed opacity-50' : ''}
                 `}
                disabled={isLoginButtonLoading}
              >
                {isLoginButtonLoading ? 'Loading...' : 'Log in to subscribe'}
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const PricingWithAITracking = withAITracking(reactPlugin, Pricing);

export { PricingWithAITracking as Pricing };
